import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import {
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { BehaviorSubject, Subject } from 'rxjs';
import { cloneDeep, isEqual } from '@fi-shared/utils';
import { takeUntil } from 'rxjs/operators';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { guid } from '@fi-shared/utils/guid';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';

export interface IChecklist2ReportRecentActivityComponentData {
    reportParameter: IChecklistV2ReportParameter;
    reportData: FiChecklist2ReportData;
    currentFilter: IChecklistV2ReportParameterFilter;
}

export interface IChecklist2ReportRecentActivityDialogResult {
    reportParameter: IChecklistV2ReportParameter;
    currentFilter: IChecklistV2ReportParameterFilter;
}

@Component({
    selector: 'fi-recent-activity-dialog',
    templateUrl: './fi-recent-activity-dialog.component.html',
    styleUrls: ['./fi-recent-activity-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-recent-activity-dialog' },
    standalone: false,
})
export class FiRecentActivityDialogComponent
    extends FiDialogsContentBase<
        IChecklist2ReportRecentActivityComponentData,
        IChecklist2ReportRecentActivityDialogResult
    >
    implements OnInit, OnDestroy
{
    filterButtonsVisibilityStatus$ = new BehaviorSubject<boolean>(false);
    reportParameter: IChecklistV2ReportParameter;
    reportData: FiChecklist2ReportData;
    currentFilter: IChecklistV2ReportParameterFilter;
    initialFilter: IChecklistV2ReportParameterFilter;
    ngOnInit() {
        this.setServices();
        this.reportParameter = cloneDeep(this.data.reportParameter);
        this.reportData = this.data.reportData;
        this.currentFilter = cloneDeep(this.data.currentFilter);
        this.initialFilter = cloneDeep(this.data.currentFilter);

        this.subscribeOnFilterButtonsVisibilityChange();
        this.subscribeOnFilterSaveFinished();
        this.fiChecklist2ReportHelperService
            .onReportParamUpdate$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((reportParam) => {
                this.reportParameter = reportParam;
                this.currentFilter = this.reportParameter.Settings.Filters.find((filter) => {
                    return filter.Guid === this.reportParameter.Settings.CurrentFilterGuid;
                });
                this.initialFilter = Object.assign({}, this.currentFilter);
            });
    }

    private subscribeOnFilterButtonsVisibilityChange() {
        this.fiChecklist2ReportHelperService
            .onSaveFilterButtonVisibilityChange$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((isVisible: boolean) => {
                if (this.fiChecklist2ReportHelperService.isFilterEmpty(this.currentFilter)) {
                    this.filterButtonsVisibilityStatus$.next(false);
                } else {
                    this.filterButtonsVisibilityStatus$.next(isVisible);
                }
            });
    }

    private subscribeOnFilterSaveFinished() {
        this.fiChecklist2ReportHelperService
            .onSaveFilterFilterFinished$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.filterButtonsVisibilityStatus$.next(false);
            });
    }

    onFilterChange(newFilter: IChecklistV2ReportParameterFilter) {
        this.currentFilter = Object.assign({}, newFilter);
        this.reportParameter.Settings.CurrentFilterGuid = this.currentFilter.Guid;
        this.fiChecklist2ReportDataService.setWidgetFilter(this.reportParameter);
    }

    onInitialFilterUpdate(newInitialFilter: IChecklistV2ReportParameterFilter) {
        this.initialFilter = Object.assign({}, newInitialFilter);
    }

    saveFilter() {
        this.fiChecklist2ReportHelperService.saveFilter({ updateCurrentFilter: false });
    }

    closeDialog() {
        if (!isEqual(this.initialFilter, this.currentFilter)) {
            this.currentFilter = Object.assign({}, this.currentFilter, { Guid: guid(), Name: '' });
            this.reportParameter.Settings.CurrentFilterGuid = this.currentFilter.Guid;
            this.fiChecklist2ReportDataService.setWidgetFilter(this.reportParameter);
        }
        this.dialogRef.close({
            value: this.getResult(),
        });
    }

    getResult(): IChecklist2ReportRecentActivityDialogResult {
        return {
            reportParameter: this.reportParameter,
            currentFilter: this.currentFilter,
        };
    }

    private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService;
    private fiChecklist2ReportDataService: FiChecklist2ReportDataService;
    private setServices() {
        this.fiChecklist2ReportHelperService = this.injector.get(FiChecklist2ReportHelperService);
        this.fiChecklist2ReportDataService = this.injector.get(FiChecklist2ReportDataService);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
